import { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper";
import { DevBoxes } from "../operationsInterfaces";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { DevCenterClient } from "../devCenterClient";
import {
  DevBox,
  DevBoxesListAllDevBoxesNextOptionalParams,
  DevBoxesListAllDevBoxesOptionalParams,
  DevBoxesListAllDevBoxesResponse,
  CustomizationTaskDefinition,
  DevBoxesListCustomizationTaskDefinitionsByProjectNextOptionalParams,
  DevBoxesListCustomizationTaskDefinitionsByProjectOptionalParams,
  DevBoxesListCustomizationTaskDefinitionsByProjectResponse,
  Pool,
  DevBoxesListPoolsNextOptionalParams,
  DevBoxesListPoolsOptionalParams,
  DevBoxesListPoolsResponse,
  Schedule,
  DevBoxesListSchedulesByPoolNextOptionalParams,
  DevBoxesListSchedulesByPoolOptionalParams,
  DevBoxesListSchedulesByPoolResponse,
  DevBoxesListSchedulesByProjectNextOptionalParams,
  DevBoxesListSchedulesByProjectOptionalParams,
  DevBoxesListSchedulesByProjectResponse,
  DevBoxesListDevBoxesByUserNextOptionalParams,
  DevBoxesListDevBoxesByUserOptionalParams,
  DevBoxesListDevBoxesByUserResponse,
  DevBoxAction,
  DevBoxesListActionsNextOptionalParams,
  DevBoxesListActionsOptionalParams,
  DevBoxesListActionsResponse,
  DevBoxActionDelayResult,
  DevBoxesDelayActionsNextOptionalParams,
  DevBoxesDelayActionsOptionalParams,
  DevBoxesDelayActionsResponse,
  CustomizationGroup,
  DevBoxesListCustomizationGroupsNextOptionalParams,
  DevBoxesListCustomizationGroupsOptionalParams,
  DevBoxesListCustomizationGroupsResponse,
  DevBoxOperationUnion,
  DevBoxesListOperationsNextOptionalParams,
  DevBoxesListOperationsOptionalParams,
  DevBoxesListOperationsResponse,
  DevBoxSnapshot,
  DevBoxesListSnapshotsNextOptionalParams,
  DevBoxesListSnapshotsOptionalParams,
  DevBoxesListSnapshotsResponse,
  DevBoxesListAllDevBoxesByUserNextOptionalParams,
  DevBoxesListAllDevBoxesByUserOptionalParams,
  DevBoxesListAllDevBoxesByUserResponse,
  DevBoxesGetCustomizationTaskDefinitionsOptionalParams,
  DevBoxesGetCustomizationTaskDefinitionsResponse,
  CustomizationTaskList,
  DevBoxesValidateCustomizationTasksActionOptionalParams,
  DevBoxesValidateCustomizationTasksActionResponse,
  DevBoxesGetImagingTaskLogOptionalParams,
  DevBoxesGetImagingTaskLogResponse,
  DevBoxesGetPoolOptionalParams,
  DevBoxesGetPoolResponse,
  DevBoxesGetScheduleByPoolOptionalParams,
  DevBoxesGetScheduleByPoolResponse,
  DevBoxesGetDevBoxByUserOptionalParams,
  DevBoxesGetDevBoxByUserResponse,
  DevBoxesCreateDevBoxOptionalParams,
  DevBoxesCreateDevBoxResponse,
  DevBoxesDeleteDevBoxOptionalParams,
  DevBoxesDeleteDevBoxResponse,
  DevBoxesStartDevBoxOptionalParams,
  DevBoxesStartDevBoxResponse,
  DevBoxesStopDevBoxOptionalParams,
  DevBoxesStopDevBoxResponse,
  DevBoxesRestartDevBoxOptionalParams,
  DevBoxesRestartDevBoxResponse,
  DevBoxesRepairDevBoxOptionalParams,
  DevBoxesRepairDevBoxResponse,
  DevBoxesRestoreSnapshotOptionalParams,
  DevBoxesRestoreSnapshotResponse,
  DevBoxesCaptureSnapshotOptionalParams,
  DevBoxesCaptureSnapshotResponse,
  DevBoxesGetActionOptionalParams,
  DevBoxesGetActionResponse,
  DevBoxesSkipActionOptionalParams,
  DevBoxesDelayActionOptionalParams,
  DevBoxesDelayActionResponse,
  DevBoxesGetCustomizationGroupOptionalParams,
  DevBoxesGetCustomizationGroupResponse,
  DevBoxesCreateCustomizationGroupOptionalParams,
  DevBoxesCreateCustomizationGroupResponse,
  DevBoxesGetCustomizationTaskLogOptionalParams,
  DevBoxesGetCustomizationTaskLogResponse,
  DevBoxesGetOperationOptionalParams,
  DevBoxesGetOperationResponse,
  DevBoxesGetRemoteConnectionOptionalParams,
  DevBoxesGetRemoteConnectionResponse,
  DevBoxesGetSnapshotOptionalParams,
  DevBoxesGetSnapshotResponse,
  DevBoxesListAllDevBoxesNextResponse,
  DevBoxesListCustomizationTaskDefinitionsByProjectNextResponse,
  DevBoxesListPoolsNextResponse,
  DevBoxesListSchedulesByPoolNextResponse,
  DevBoxesListSchedulesByProjectNextResponse,
  DevBoxesListDevBoxesByUserNextResponse,
  DevBoxesListActionsNextResponse,
  DevBoxesDelayActionsNextResponse,
  DevBoxesListCustomizationGroupsNextResponse,
  DevBoxesListOperationsNextResponse,
  DevBoxesListSnapshotsNextResponse,
  DevBoxesListAllDevBoxesByUserNextResponse,
} from "../models";

/// <reference lib="esnext.asynciterable" />
/** Class containing DevBoxes operations. */
export class DevBoxesImpl implements DevBoxes {
  private readonly client: DevCenterClient;

  /**
   * Initialize a new instance of the class DevBoxes class.
   * @param client Reference to the service client
   */
  constructor(client: DevCenterClient) {
    this.client = client;
  }

  /**
   * Lists Dev Boxes that the caller has access to in the DevCenter.
   * @param options The options parameters.
   */
  public listAllDevBoxes(
    options?: DevBoxesListAllDevBoxesOptionalParams,
  ): PagedAsyncIterableIterator<DevBox> {
    const iter = this.listAllDevBoxesPagingAll(options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listAllDevBoxesPagingPage(options, settings);
      },
    };
  }

  private async *listAllDevBoxesPagingPage(
    options?: DevBoxesListAllDevBoxesOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBox[]> {
    let result: DevBoxesListAllDevBoxesResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listAllDevBoxes(options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listAllDevBoxesNext(continuationToken, options);
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listAllDevBoxesPagingAll(
    options?: DevBoxesListAllDevBoxesOptionalParams,
  ): AsyncIterableIterator<DevBox> {
    for await (const page of this.listAllDevBoxesPagingPage(options)) {
      yield* page;
    }
  }

  /**
   * Lists all customization tasks available to the project.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  public listCustomizationTaskDefinitionsByProject(
    projectName: string,
    options?: DevBoxesListCustomizationTaskDefinitionsByProjectOptionalParams,
  ): PagedAsyncIterableIterator<CustomizationTaskDefinition> {
    const iter = this.listCustomizationTaskDefinitionsByProjectPagingAll(
      projectName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listCustomizationTaskDefinitionsByProjectPagingPage(
          projectName,
          options,
          settings,
        );
      },
    };
  }

  private async *listCustomizationTaskDefinitionsByProjectPagingPage(
    projectName: string,
    options?: DevBoxesListCustomizationTaskDefinitionsByProjectOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<CustomizationTaskDefinition[]> {
    let result: DevBoxesListCustomizationTaskDefinitionsByProjectResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listCustomizationTaskDefinitionsByProject(
        projectName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listCustomizationTaskDefinitionsByProjectNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listCustomizationTaskDefinitionsByProjectPagingAll(
    projectName: string,
    options?: DevBoxesListCustomizationTaskDefinitionsByProjectOptionalParams,
  ): AsyncIterableIterator<CustomizationTaskDefinition> {
    for await (const page of this.listCustomizationTaskDefinitionsByProjectPagingPage(
      projectName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists available pools.
   * @param projectName Name of the project.
   * @param options The options parameters.
   */
  public listPools(
    projectName: string,
    options?: DevBoxesListPoolsOptionalParams,
  ): PagedAsyncIterableIterator<Pool> {
    const iter = this.listPoolsPagingAll(projectName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPoolsPagingPage(projectName, options, settings);
      },
    };
  }

  private async *listPoolsPagingPage(
    projectName: string,
    options?: DevBoxesListPoolsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Pool[]> {
    let result: DevBoxesListPoolsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listPools(projectName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listPoolsNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPoolsPagingAll(
    projectName: string,
    options?: DevBoxesListPoolsOptionalParams,
  ): AsyncIterableIterator<Pool> {
    for await (const page of this.listPoolsPagingPage(projectName, options)) {
      yield* page;
    }
  }

  /**
   * Lists all schedules within a pool that are configured by your project administrator.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param poolName The name of a pool of Dev Boxes.
   * @param options The options parameters.
   */
  public listSchedulesByPool(
    projectName: string,
    poolName: string,
    options?: DevBoxesListSchedulesByPoolOptionalParams,
  ): PagedAsyncIterableIterator<Schedule> {
    const iter = this.listSchedulesByPoolPagingAll(
      projectName,
      poolName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listSchedulesByPoolPagingPage(
          projectName,
          poolName,
          options,
          settings,
        );
      },
    };
  }

  private async *listSchedulesByPoolPagingPage(
    projectName: string,
    poolName: string,
    options?: DevBoxesListSchedulesByPoolOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Schedule[]> {
    let result: DevBoxesListSchedulesByPoolResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listSchedulesByPool(projectName, poolName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listSchedulesByPoolNext(
        projectName,
        poolName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listSchedulesByPoolPagingAll(
    projectName: string,
    poolName: string,
    options?: DevBoxesListSchedulesByPoolOptionalParams,
  ): AsyncIterableIterator<Schedule> {
    for await (const page of this.listSchedulesByPoolPagingPage(
      projectName,
      poolName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists all schedules within a project that are configured by your project administrator.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  public listSchedulesByProject(
    projectName: string,
    options?: DevBoxesListSchedulesByProjectOptionalParams,
  ): PagedAsyncIterableIterator<Schedule> {
    const iter = this.listSchedulesByProjectPagingAll(projectName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listSchedulesByProjectPagingPage(
          projectName,
          options,
          settings,
        );
      },
    };
  }

  private async *listSchedulesByProjectPagingPage(
    projectName: string,
    options?: DevBoxesListSchedulesByProjectOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<Schedule[]> {
    let result: DevBoxesListSchedulesByProjectResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listSchedulesByProject(projectName, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listSchedulesByProjectNext(
        projectName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listSchedulesByProjectPagingAll(
    projectName: string,
    options?: DevBoxesListSchedulesByProjectOptionalParams,
  ): AsyncIterableIterator<Schedule> {
    for await (const page of this.listSchedulesByProjectPagingPage(
      projectName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists Dev Boxes in the project for a particular user.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param options The options parameters.
   */
  public listDevBoxesByUser(
    projectName: string,
    userId: string,
    options?: DevBoxesListDevBoxesByUserOptionalParams,
  ): PagedAsyncIterableIterator<DevBox> {
    const iter = this.listDevBoxesByUserPagingAll(projectName, userId, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listDevBoxesByUserPagingPage(
          projectName,
          userId,
          options,
          settings,
        );
      },
    };
  }

  private async *listDevBoxesByUserPagingPage(
    projectName: string,
    userId: string,
    options?: DevBoxesListDevBoxesByUserOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBox[]> {
    let result: DevBoxesListDevBoxesByUserResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listDevBoxesByUser(projectName, userId, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listDevBoxesByUserNext(
        projectName,
        userId,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listDevBoxesByUserPagingAll(
    projectName: string,
    userId: string,
    options?: DevBoxesListDevBoxesByUserOptionalParams,
  ): AsyncIterableIterator<DevBox> {
    for await (const page of this.listDevBoxesByUserPagingPage(
      projectName,
      userId,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists actions on a Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  public listActions(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListActionsOptionalParams,
  ): PagedAsyncIterableIterator<DevBoxAction> {
    const iter = this.listActionsPagingAll(
      projectName,
      userId,
      devBoxName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listActionsPagingPage(
          projectName,
          userId,
          devBoxName,
          options,
          settings,
        );
      },
    };
  }

  private async *listActionsPagingPage(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListActionsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBoxAction[]> {
    let result: DevBoxesListActionsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listActions(
        projectName,
        userId,
        devBoxName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listActionsNext(
        projectName,
        userId,
        devBoxName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listActionsPagingAll(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListActionsOptionalParams,
  ): AsyncIterableIterator<DevBoxAction> {
    for await (const page of this.listActionsPagingPage(
      projectName,
      userId,
      devBoxName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Delays all actions.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param delayUntil The time to delay the Dev Box action or actions until, in RFC3339 format.
   * @param options The options parameters.
   */
  public listDelayActions(
    projectName: string,
    userId: string,
    devBoxName: string,
    delayUntil: Date,
    options?: DevBoxesDelayActionsOptionalParams,
  ): PagedAsyncIterableIterator<DevBoxActionDelayResult> {
    const iter = this.delayActionsPagingAll(
      projectName,
      userId,
      devBoxName,
      delayUntil,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.delayActionsPagingPage(
          projectName,
          userId,
          devBoxName,
          delayUntil,
          options,
          settings,
        );
      },
    };
  }

  private async *delayActionsPagingPage(
    projectName: string,
    userId: string,
    devBoxName: string,
    delayUntil: Date,
    options?: DevBoxesDelayActionsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBoxActionDelayResult[]> {
    let result: DevBoxesDelayActionsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._delayActions(
        projectName,
        userId,
        devBoxName,
        delayUntil,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._delayActionsNext(
        projectName,
        userId,
        devBoxName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *delayActionsPagingAll(
    projectName: string,
    userId: string,
    devBoxName: string,
    delayUntil: Date,
    options?: DevBoxesDelayActionsOptionalParams,
  ): AsyncIterableIterator<DevBoxActionDelayResult> {
    for await (const page of this.delayActionsPagingPage(
      projectName,
      userId,
      devBoxName,
      delayUntil,
      options,
    )) {
      yield* page;
    }
  }

  /**
   *   Lists customization groups on the Dev Box. Listed customization groups exclude
   *   task information unless specified via the include parameter.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  public listCustomizationGroups(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListCustomizationGroupsOptionalParams,
  ): PagedAsyncIterableIterator<CustomizationGroup> {
    const iter = this.listCustomizationGroupsPagingAll(
      projectName,
      userId,
      devBoxName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listCustomizationGroupsPagingPage(
          projectName,
          userId,
          devBoxName,
          options,
          settings,
        );
      },
    };
  }

  private async *listCustomizationGroupsPagingPage(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListCustomizationGroupsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<CustomizationGroup[]> {
    let result: DevBoxesListCustomizationGroupsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listCustomizationGroups(
        projectName,
        userId,
        devBoxName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listCustomizationGroupsNext(
        projectName,
        userId,
        devBoxName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listCustomizationGroupsPagingAll(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListCustomizationGroupsOptionalParams,
  ): AsyncIterableIterator<CustomizationGroup> {
    for await (const page of this.listCustomizationGroupsPagingPage(
      projectName,
      userId,
      devBoxName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists operations on the Dev Box which have occurred within the past 90 days.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  public listOperations(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListOperationsOptionalParams,
  ): PagedAsyncIterableIterator<DevBoxOperationUnion> {
    const iter = this.listOperationsPagingAll(
      projectName,
      userId,
      devBoxName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listOperationsPagingPage(
          projectName,
          userId,
          devBoxName,
          options,
          settings,
        );
      },
    };
  }

  private async *listOperationsPagingPage(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListOperationsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBoxOperationUnion[]> {
    let result: DevBoxesListOperationsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listOperations(
        projectName,
        userId,
        devBoxName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listOperationsNext(
        projectName,
        userId,
        devBoxName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listOperationsPagingAll(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListOperationsOptionalParams,
  ): AsyncIterableIterator<DevBoxOperationUnion> {
    for await (const page of this.listOperationsPagingPage(
      projectName,
      userId,
      devBoxName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists snapshots for this Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  public listSnapshots(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListSnapshotsOptionalParams,
  ): PagedAsyncIterableIterator<DevBoxSnapshot> {
    const iter = this.listSnapshotsPagingAll(
      projectName,
      userId,
      devBoxName,
      options,
    );
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listSnapshotsPagingPage(
          projectName,
          userId,
          devBoxName,
          options,
          settings,
        );
      },
    };
  }

  private async *listSnapshotsPagingPage(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListSnapshotsOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBoxSnapshot[]> {
    let result: DevBoxesListSnapshotsResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listSnapshots(
        projectName,
        userId,
        devBoxName,
        options,
      );
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listSnapshotsNext(
        projectName,
        userId,
        devBoxName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listSnapshotsPagingAll(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListSnapshotsOptionalParams,
  ): AsyncIterableIterator<DevBoxSnapshot> {
    for await (const page of this.listSnapshotsPagingPage(
      projectName,
      userId,
      devBoxName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists Dev Boxes in the Dev Center for a particular user.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param options The options parameters.
   */
  public listAllDevBoxesByUser(
    userId: string,
    options?: DevBoxesListAllDevBoxesByUserOptionalParams,
  ): PagedAsyncIterableIterator<DevBox> {
    const iter = this.listAllDevBoxesByUserPagingAll(userId, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listAllDevBoxesByUserPagingPage(userId, options, settings);
      },
    };
  }

  private async *listAllDevBoxesByUserPagingPage(
    userId: string,
    options?: DevBoxesListAllDevBoxesByUserOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<DevBox[]> {
    let result: DevBoxesListAllDevBoxesByUserResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._listAllDevBoxesByUser(userId, options);
      let page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listAllDevBoxesByUserNext(
        userId,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      let page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listAllDevBoxesByUserPagingAll(
    userId: string,
    options?: DevBoxesListAllDevBoxesByUserOptionalParams,
  ): AsyncIterableIterator<DevBox> {
    for await (const page of this.listAllDevBoxesByUserPagingPage(
      userId,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Lists Dev Boxes that the caller has access to in the DevCenter.
   * @param options The options parameters.
   */
  private _listAllDevBoxes(
    options?: DevBoxesListAllDevBoxesOptionalParams,
  ): Promise<DevBoxesListAllDevBoxesResponse> {
    return this.client.sendOperationRequest(
      { options },
      listAllDevBoxesOperationSpec,
    );
  }

  /**
   * Gets a customization task.
   * @param projectName Name of the project.
   * @param catalogName Name of the catalog.
   * @param taskName Full name of the task: {catalogName}/{taskName}.
   * @param options The options parameters.
   */
  getCustomizationTaskDefinitions(
    projectName: string,
    catalogName: string,
    taskName: string,
    options?: DevBoxesGetCustomizationTaskDefinitionsOptionalParams,
  ): Promise<DevBoxesGetCustomizationTaskDefinitionsResponse> {
    return this.client.sendOperationRequest(
      { projectName, catalogName, taskName, options },
      getCustomizationTaskDefinitionsOperationSpec,
    );
  }

  /**
   * Lists all customization tasks available to the project.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  private _listCustomizationTaskDefinitionsByProject(
    projectName: string,
    options?: DevBoxesListCustomizationTaskDefinitionsByProjectOptionalParams,
  ): Promise<DevBoxesListCustomizationTaskDefinitionsByProjectResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listCustomizationTaskDefinitionsByProjectOperationSpec,
    );
  }

  /**
   * Validates a list of customization tasks.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param body Customization tasks to validate.
   * @param options The options parameters.
   */
  validateCustomizationTasksAction(
    projectName: string,
    body: CustomizationTaskList,
    options?: DevBoxesValidateCustomizationTasksActionOptionalParams,
  ): Promise<DevBoxesValidateCustomizationTasksActionResponse> {
    return this.client.sendOperationRequest(
      { projectName, body, options },
      validateCustomizationTasksActionOperationSpec,
    );
  }

  /**
   * Gets the log for an imaging build task.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param imageBuildLogId An imaging build log id.
   * @param options The options parameters.
   */
  getImagingTaskLog(
    projectName: string,
    imageBuildLogId: string,
    options?: DevBoxesGetImagingTaskLogOptionalParams,
  ): Promise<DevBoxesGetImagingTaskLogResponse> {
    return this.client.sendOperationRequest(
      { projectName, imageBuildLogId, options },
      getImagingTaskLogOperationSpec,
    );
  }

  /**
   * Lists available pools.
   * @param projectName Name of the project.
   * @param options The options parameters.
   */
  private _listPools(
    projectName: string,
    options?: DevBoxesListPoolsOptionalParams,
  ): Promise<DevBoxesListPoolsResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listPoolsOperationSpec,
    );
  }

  /**
   * Gets a pool.
   * @param projectName Name of the project.
   * @param poolName Pool name.
   * @param options The options parameters.
   */
  getPool(
    projectName: string,
    poolName: string,
    options?: DevBoxesGetPoolOptionalParams,
  ): Promise<DevBoxesGetPoolResponse> {
    return this.client.sendOperationRequest(
      { projectName, poolName, options },
      getPoolOperationSpec,
    );
  }

  /**
   * Lists all schedules within a pool that are configured by your project administrator.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param poolName The name of a pool of Dev Boxes.
   * @param options The options parameters.
   */
  private _listSchedulesByPool(
    projectName: string,
    poolName: string,
    options?: DevBoxesListSchedulesByPoolOptionalParams,
  ): Promise<DevBoxesListSchedulesByPoolResponse> {
    return this.client.sendOperationRequest(
      { projectName, poolName, options },
      listSchedulesByPoolOperationSpec,
    );
  }

  /**
   * Gets a schedule.
   * @param projectName Name of the project.
   * @param poolName Pool name.
   * @param scheduleName Display name for the Schedule.
   * @param options The options parameters.
   */
  getScheduleByPool(
    projectName: string,
    poolName: string,
    scheduleName: string,
    options?: DevBoxesGetScheduleByPoolOptionalParams,
  ): Promise<DevBoxesGetScheduleByPoolResponse> {
    return this.client.sendOperationRequest(
      { projectName, poolName, scheduleName, options },
      getScheduleByPoolOperationSpec,
    );
  }

  /**
   * Lists all schedules within a project that are configured by your project administrator.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param options The options parameters.
   */
  private _listSchedulesByProject(
    projectName: string,
    options?: DevBoxesListSchedulesByProjectOptionalParams,
  ): Promise<DevBoxesListSchedulesByProjectResponse> {
    return this.client.sendOperationRequest(
      { projectName, options },
      listSchedulesByProjectOperationSpec,
    );
  }

  /**
   * Lists Dev Boxes in the project for a particular user.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param options The options parameters.
   */
  private _listDevBoxesByUser(
    projectName: string,
    userId: string,
    options?: DevBoxesListDevBoxesByUserOptionalParams,
  ): Promise<DevBoxesListDevBoxesByUserResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, options },
      listDevBoxesByUserOperationSpec,
    );
  }

  /**
   * Gets a Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  getDevBoxByUser(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesGetDevBoxByUserOptionalParams,
  ): Promise<DevBoxesGetDevBoxByUserResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      getDevBoxByUserOperationSpec,
    );
  }

  /**
   * Creates or replaces a Dev Box.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param body Represents the body request of a Dev Box creation. Dev Box Pool name is required.
   *             Optionally set the owner of the Dev Box as local administrator
   * @param options The options parameters.
   */
  createDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    body: DevBox,
    options?: DevBoxesCreateDevBoxOptionalParams,
  ): Promise<DevBoxesCreateDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, body, options },
      createDevBoxOperationSpec,
    );
  }

  /**
   * Deletes a Dev Box.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  deleteDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesDeleteDevBoxOptionalParams,
  ): Promise<DevBoxesDeleteDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      deleteDevBoxOperationSpec,
    );
  }

  /**
   * Starts a Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  startDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesStartDevBoxOptionalParams,
  ): Promise<DevBoxesStartDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      startDevBoxOperationSpec,
    );
  }

  /**
   * Stops a Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  stopDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesStopDevBoxOptionalParams,
  ): Promise<DevBoxesStopDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      stopDevBoxOperationSpec,
    );
  }

  /**
   * Restarts a Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  restartDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesRestartDevBoxOptionalParams,
  ): Promise<DevBoxesRestartDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      restartDevBoxOperationSpec,
    );
  }

  /**
   * Attempts automated repair steps to resolve common problems on a Dev Box. The Dev Box may restart
   * during this operation.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  repairDevBox(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesRepairDevBoxOptionalParams,
  ): Promise<DevBoxesRepairDevBoxResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      repairDevBoxOperationSpec,
    );
  }

  /**
   * Restores a Dev Box to a specified snapshot.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param snapshotId Required parameter that specifies the snapshot id to use for the restore
   *                   operation.
   * @param options The options parameters.
   */
  restoreSnapshot(
    projectName: string,
    userId: string,
    devBoxName: string,
    snapshotId: string,
    options?: DevBoxesRestoreSnapshotOptionalParams,
  ): Promise<DevBoxesRestoreSnapshotResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, snapshotId, options },
      restoreSnapshotOperationSpec,
    );
  }

  /**
   * Captures a manual snapshot of the Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  captureSnapshot(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesCaptureSnapshotOptionalParams,
  ): Promise<DevBoxesCaptureSnapshotResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      captureSnapshotOperationSpec,
    );
  }

  /**
   * Lists actions on a Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  private _listActions(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListActionsOptionalParams,
  ): Promise<DevBoxesListActionsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      listActionsOperationSpec,
    );
  }

  /**
   * Gets an action.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param actionName The name of the action.
   * @param options The options parameters.
   */
  getAction(
    projectName: string,
    userId: string,
    devBoxName: string,
    actionName: string,
    options?: DevBoxesGetActionOptionalParams,
  ): Promise<DevBoxesGetActionResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, actionName, options },
      getActionOperationSpec,
    );
  }

  /**
   * Skips an occurrence of an action.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param actionName The name of the action.
   * @param options The options parameters.
   */
  skipAction(
    projectName: string,
    userId: string,
    devBoxName: string,
    actionName: string,
    options?: DevBoxesSkipActionOptionalParams,
  ): Promise<void> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, actionName, options },
      skipActionOperationSpec,
    );
  }

  /**
   * Delays the occurrence of an action.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param actionName The name of the action.
   * @param delayUntil The time to delay the Dev Box action or actions until, in RFC3339 format.
   * @param options The options parameters.
   */
  delayAction(
    projectName: string,
    userId: string,
    devBoxName: string,
    actionName: string,
    delayUntil: Date,
    options?: DevBoxesDelayActionOptionalParams,
  ): Promise<DevBoxesDelayActionResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, actionName, delayUntil, options },
      delayActionOperationSpec,
    );
  }

  /**
   * Delays all actions.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param delayUntil The time to delay the Dev Box action or actions until, in RFC3339 format.
   * @param options The options parameters.
   */
  private _delayActions(
    projectName: string,
    userId: string,
    devBoxName: string,
    delayUntil: Date,
    options?: DevBoxesDelayActionsOptionalParams,
  ): Promise<DevBoxesDelayActionsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, delayUntil, options },
      delayActionsOperationSpec,
    );
  }

  /**
   *   Lists customization groups on the Dev Box. Listed customization groups exclude
   *   task information unless specified via the include parameter.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  private _listCustomizationGroups(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListCustomizationGroupsOptionalParams,
  ): Promise<DevBoxesListCustomizationGroupsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      listCustomizationGroupsOperationSpec,
    );
  }

  /**
   *   Gets a customization group.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param customizationGroupName Name of the customization group.
   * @param options The options parameters.
   */
  getCustomizationGroup(
    projectName: string,
    userId: string,
    devBoxName: string,
    customizationGroupName: string,
    options?: DevBoxesGetCustomizationGroupOptionalParams,
  ): Promise<DevBoxesGetCustomizationGroupResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, customizationGroupName, options },
      getCustomizationGroupOperationSpec,
    );
  }

  /**
   * Applies customizations to the Dev Box.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param customizationGroupName A customization group name.
   * @param body Represents the body request of a Dev Box creation. Dev Box Pool name is required.
   *             Optionally set the owner of the Dev Box as local administrator
   * @param options The options parameters.
   */
  createCustomizationGroup(
    projectName: string,
    userId: string,
    devBoxName: string,
    customizationGroupName: string,
    body: CustomizationGroup,
    options?: DevBoxesCreateCustomizationGroupOptionalParams,
  ): Promise<DevBoxesCreateCustomizationGroupResponse> {
    return this.client.sendOperationRequest(
      {
        projectName,
        userId,
        devBoxName,
        customizationGroupName,
        body,
        options,
      },
      createCustomizationGroupOperationSpec,
    );
  }

  /**
   * Gets the log for a customization task.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param customizationGroupName A customization group name.
   * @param customizationTaskId A customization task ID.
   * @param options The options parameters.
   */
  getCustomizationTaskLog(
    projectName: string,
    userId: string,
    devBoxName: string,
    customizationGroupName: string,
    customizationTaskId: string,
    options?: DevBoxesGetCustomizationTaskLogOptionalParams,
  ): Promise<DevBoxesGetCustomizationTaskLogResponse> {
    return this.client.sendOperationRequest(
      {
        projectName,
        userId,
        devBoxName,
        customizationGroupName,
        customizationTaskId,
        options,
      },
      getCustomizationTaskLogOperationSpec,
    );
  }

  /**
   * Lists operations on the Dev Box which have occurred within the past 90 days.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  private _listOperations(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListOperationsOptionalParams,
  ): Promise<DevBoxesListOperationsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      listOperationsOperationSpec,
    );
  }

  /**
   * Gets an operation on a Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param operationId Unique identifier for the Dev Box operation.
   * @param options The options parameters.
   */
  getOperation(
    projectName: string,
    userId: string,
    devBoxName: string,
    operationId: string,
    options?: DevBoxesGetOperationOptionalParams,
  ): Promise<DevBoxesGetOperationResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, operationId, options },
      getOperationOperationSpec,
    );
  }

  /**
   * Gets RDP Connection info.
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName The name of a Dev Box.
   * @param options The options parameters.
   */
  getRemoteConnection(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesGetRemoteConnectionOptionalParams,
  ): Promise<DevBoxesGetRemoteConnectionResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      getRemoteConnectionOperationSpec,
    );
  }

  /**
   * Lists snapshots for this Dev Box.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param options The options parameters.
   */
  private _listSnapshots(
    projectName: string,
    userId: string,
    devBoxName: string,
    options?: DevBoxesListSnapshotsOptionalParams,
  ): Promise<DevBoxesListSnapshotsResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, options },
      listSnapshotsOperationSpec,
    );
  }

  /**
   * Gets a snapshot by snapshot id.
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param snapshotId The id of the snapshot. Should be treated as opaque string.
   * @param options The options parameters.
   */
  getSnapshot(
    projectName: string,
    userId: string,
    devBoxName: string,
    snapshotId: string,
    options?: DevBoxesGetSnapshotOptionalParams,
  ): Promise<DevBoxesGetSnapshotResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, snapshotId, options },
      getSnapshotOperationSpec,
    );
  }

  /**
   * Lists Dev Boxes in the Dev Center for a particular user.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param options The options parameters.
   */
  private _listAllDevBoxesByUser(
    userId: string,
    options?: DevBoxesListAllDevBoxesByUserOptionalParams,
  ): Promise<DevBoxesListAllDevBoxesByUserResponse> {
    return this.client.sendOperationRequest(
      { userId, options },
      listAllDevBoxesByUserOperationSpec,
    );
  }

  /**
   * ListAllDevBoxesNext
   * @param nextLink The nextLink from the previous successful call to the ListAllDevBoxes method.
   * @param options The options parameters.
   */
  private _listAllDevBoxesNext(
    nextLink: string,
    options?: DevBoxesListAllDevBoxesNextOptionalParams,
  ): Promise<DevBoxesListAllDevBoxesNextResponse> {
    return this.client.sendOperationRequest(
      { nextLink, options },
      listAllDevBoxesNextOperationSpec,
    );
  }

  /**
   * ListCustomizationTaskDefinitionsByProjectNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param nextLink The nextLink from the previous successful call to the
   *                 ListCustomizationTaskDefinitionsByProject method.
   * @param options The options parameters.
   */
  private _listCustomizationTaskDefinitionsByProjectNext(
    projectName: string,
    nextLink: string,
    options?: DevBoxesListCustomizationTaskDefinitionsByProjectNextOptionalParams,
  ): Promise<DevBoxesListCustomizationTaskDefinitionsByProjectNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listCustomizationTaskDefinitionsByProjectNextOperationSpec,
    );
  }

  /**
   * ListPoolsNext
   * @param projectName Name of the project.
   * @param nextLink The nextLink from the previous successful call to the ListPools method.
   * @param options The options parameters.
   */
  private _listPoolsNext(
    projectName: string,
    nextLink: string,
    options?: DevBoxesListPoolsNextOptionalParams,
  ): Promise<DevBoxesListPoolsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listPoolsNextOperationSpec,
    );
  }

  /**
   * ListSchedulesByPoolNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param poolName The name of a pool of Dev Boxes.
   * @param nextLink The nextLink from the previous successful call to the ListSchedulesByPool method.
   * @param options The options parameters.
   */
  private _listSchedulesByPoolNext(
    projectName: string,
    poolName: string,
    nextLink: string,
    options?: DevBoxesListSchedulesByPoolNextOptionalParams,
  ): Promise<DevBoxesListSchedulesByPoolNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, poolName, nextLink, options },
      listSchedulesByPoolNextOperationSpec,
    );
  }

  /**
   * ListSchedulesByProjectNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param nextLink The nextLink from the previous successful call to the ListSchedulesByProject method.
   * @param options The options parameters.
   */
  private _listSchedulesByProjectNext(
    projectName: string,
    nextLink: string,
    options?: DevBoxesListSchedulesByProjectNextOptionalParams,
  ): Promise<DevBoxesListSchedulesByProjectNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, nextLink, options },
      listSchedulesByProjectNextOperationSpec,
    );
  }

  /**
   * ListDevBoxesByUserNext
   * @param projectName The DevCenter Project upon which to execute operations.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param nextLink The nextLink from the previous successful call to the ListDevBoxesByUser method.
   * @param options The options parameters.
   */
  private _listDevBoxesByUserNext(
    projectName: string,
    userId: string,
    nextLink: string,
    options?: DevBoxesListDevBoxesByUserNextOptionalParams,
  ): Promise<DevBoxesListDevBoxesByUserNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, nextLink, options },
      listDevBoxesByUserNextOperationSpec,
    );
  }

  /**
   * ListActionsNext
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param nextLink The nextLink from the previous successful call to the ListActions method.
   * @param options The options parameters.
   */
  private _listActionsNext(
    projectName: string,
    userId: string,
    devBoxName: string,
    nextLink: string,
    options?: DevBoxesListActionsNextOptionalParams,
  ): Promise<DevBoxesListActionsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, nextLink, options },
      listActionsNextOperationSpec,
    );
  }

  /**
   * DelayActionsNext
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param nextLink The nextLink from the previous successful call to the DelayActions method.
   * @param options The options parameters.
   */
  private _delayActionsNext(
    projectName: string,
    userId: string,
    devBoxName: string,
    nextLink: string,
    options?: DevBoxesDelayActionsNextOptionalParams,
  ): Promise<DevBoxesDelayActionsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, nextLink, options },
      delayActionsNextOperationSpec,
    );
  }

  /**
   * ListCustomizationGroupsNext
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param nextLink The nextLink from the previous successful call to the ListCustomizationGroups
   *                 method.
   * @param options The options parameters.
   */
  private _listCustomizationGroupsNext(
    projectName: string,
    userId: string,
    devBoxName: string,
    nextLink: string,
    options?: DevBoxesListCustomizationGroupsNextOptionalParams,
  ): Promise<DevBoxesListCustomizationGroupsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, nextLink, options },
      listCustomizationGroupsNextOperationSpec,
    );
  }

  /**
   * ListOperationsNext
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param nextLink The nextLink from the previous successful call to the ListOperations method.
   * @param options The options parameters.
   */
  private _listOperationsNext(
    projectName: string,
    userId: string,
    devBoxName: string,
    nextLink: string,
    options?: DevBoxesListOperationsNextOptionalParams,
  ): Promise<DevBoxesListOperationsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, nextLink, options },
      listOperationsNextOperationSpec,
    );
  }

  /**
   * ListSnapshotsNext
   * @param projectName Name of the project.
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param devBoxName Display name for the Dev Box.
   * @param nextLink The nextLink from the previous successful call to the ListSnapshots method.
   * @param options The options parameters.
   */
  private _listSnapshotsNext(
    projectName: string,
    userId: string,
    devBoxName: string,
    nextLink: string,
    options?: DevBoxesListSnapshotsNextOptionalParams,
  ): Promise<DevBoxesListSnapshotsNextResponse> {
    return this.client.sendOperationRequest(
      { projectName, userId, devBoxName, nextLink, options },
      listSnapshotsNextOperationSpec,
    );
  }

  /**
   * ListAllDevBoxesByUserNext
   * @param userId The AAD object id of the user. If value is 'me', the identity is taken from the
   *               authentication context.
   * @param nextLink The nextLink from the previous successful call to the ListAllDevBoxesByUser method.
   * @param options The options parameters.
   */
  private _listAllDevBoxesByUserNext(
    userId: string,
    nextLink: string,
    options?: DevBoxesListAllDevBoxesByUserNextOptionalParams,
  ): Promise<DevBoxesListAllDevBoxesByUserNextResponse> {
    return this.client.sendOperationRequest(
      { userId, nextLink, options },
      listAllDevBoxesByUserNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listAllDevBoxesOperationSpec: coreClient.OperationSpec = {
  path: "/devboxes",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBox,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListAllDevBoxesExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint],
  headerParameters: [Parameters.accept],
  serializer,
};
const getCustomizationTaskDefinitionsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/catalogs/{catalogName}/customizationTasks/{taskName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.CustomizationTaskDefinition,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper:
        Mappers.DevBoxesGetCustomizationTaskDefinitionsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.catalogName,
    Parameters.taskName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCustomizationTaskDefinitionsByProjectOperationSpec: coreClient.OperationSpec =
  {
    path: "/projects/{projectName}/customizationTasks",
    httpMethod: "GET",
    responses: {
      200: {
        bodyMapper: Mappers.PagedCustomizationTaskDefinition,
      },
      default: {
        bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
        headersMapper:
          Mappers.DevBoxesListCustomizationTaskDefinitionsByProjectExceptionHeaders,
      },
    },
    queryParameters: [Parameters.apiVersion],
    urlParameters: [Parameters.endpoint, Parameters.projectName],
    headerParameters: [Parameters.accept],
    serializer,
  };
const validateCustomizationTasksActionOperationSpec: coreClient.OperationSpec =
  {
    path: "/projects/{projectName}/customizationTasks:validateGroup",
    httpMethod: "POST",
    responses: {
      202: {
        bodyMapper: Mappers.CustomizationTaskListValidationOperationResult,
        headersMapper: Mappers.DevBoxesValidateCustomizationTasksActionHeaders,
      },
      default: {
        bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
        headersMapper:
          Mappers.DevBoxesValidateCustomizationTasksActionExceptionHeaders,
      },
    },
    requestBody: Parameters.body,
    queryParameters: [Parameters.apiVersion],
    urlParameters: [Parameters.endpoint, Parameters.projectName],
    headerParameters: [Parameters.accept, Parameters.contentType],
    mediaType: "json",
    serializer,
  };
const getImagingTaskLogOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/imageBuildLogs/{imageBuildLogId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetImagingTaskLogExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.imageBuildLogId,
  ],
  headerParameters: [Parameters.accept1],
  serializer,
};
const listPoolsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/pools",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedPool,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListPoolsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.projectName],
  headerParameters: [Parameters.accept],
  serializer,
};
const getPoolOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/pools/{poolName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Pool,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetPoolExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.poolName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSchedulesByPoolOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/pools/{poolName}/schedules",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedSchedule,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListSchedulesByPoolExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.poolName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getScheduleByPoolOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/pools/{poolName}/schedules/{scheduleName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.Schedule,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetScheduleByPoolExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.poolName,
    Parameters.scheduleName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSchedulesByProjectOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/schedules",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedSchedule,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListSchedulesByProjectExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.projectName],
  headerParameters: [Parameters.accept],
  serializer,
};
const listDevBoxesByUserOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBox,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListDevBoxesByUserExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getDevBoxByUserOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBox,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetDevBoxByUserExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.DevBox,
    },
    201: {
      bodyMapper: Mappers.DevBox,
      headersMapper: Mappers.DevBoxesCreateDevBoxHeaders,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesCreateDevBoxExceptionHeaders,
    },
  },
  requestBody: Parameters.body1,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const deleteDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}",
  httpMethod: "DELETE",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesDeleteDevBoxHeaders,
    },
    204: {},
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesDeleteDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const startDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:start",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesStartDevBoxHeaders,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesStartDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const stopDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:stop",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesStopDevBoxHeaders,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesStopDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.hibernate],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const restartDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:restart",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesRestartDevBoxHeaders,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesRestartDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const repairDevBoxOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:repair",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesRepairDevBoxHeaders,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesRepairDevBoxExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const restoreSnapshotOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:restoreSnapshot",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesRestoreSnapshotHeaders,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesRestoreSnapshotExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.snapshotId],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const captureSnapshotOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}:captureSnapshot",
  httpMethod: "POST",
  responses: {
    202: {
      bodyMapper: Mappers.OperationStatus,
      headersMapper: Mappers.DevBoxesCaptureSnapshotHeaders,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesCaptureSnapshotExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listActionsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBoxAction,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListActionsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getActionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions/{actionName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxAction,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetActionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.actionName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const skipActionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions/{actionName}:skip",
  httpMethod: "POST",
  responses: {
    204: {},
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesSkipActionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.actionName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const delayActionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions/{actionName}:delay",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxAction,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesDelayActionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.delayUntil],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.actionName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const delayActionsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/actions:delay",
  httpMethod: "POST",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBoxActionDelayResult,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesDelayActionsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.delayUntil],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCustomizationGroupsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/customizationGroups",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedCustomizationGroup,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListCustomizationGroupsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion, Parameters.include],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getCustomizationGroupOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/customizationGroups/{customizationGroupName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.CustomizationGroup,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetCustomizationGroupExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.customizationGroupName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createCustomizationGroupOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/customizationGroups/{customizationGroupName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.CustomizationGroup,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesCreateCustomizationGroupExceptionHeaders,
    },
  },
  requestBody: Parameters.body2,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.customizationGroupName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getCustomizationTaskLogOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/customizationGroups/{customizationGroupName}/logs/{customizationTaskId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: { type: { name: "String" } },
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetCustomizationTaskLogExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.customizationGroupName,
    Parameters.customizationTaskId,
  ],
  headerParameters: [Parameters.accept1],
  serializer,
};
const listOperationsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/operations",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBoxOperation,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListOperationsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getOperationOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/operations/{operationId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxOperation,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetOperationExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.operationId,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getRemoteConnectionOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/remoteConnection",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.RemoteConnection,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetRemoteConnectionExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSnapshotsOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/snapshots",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBoxSnapshot,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListSnapshotsExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const getSnapshotOperationSpec: coreClient.OperationSpec = {
  path: "/projects/{projectName}/users/{userId}/devboxes/{devBoxName}/snapshots/{snapshotId}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.DevBoxSnapshot,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesGetSnapshotExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.snapshotId1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAllDevBoxesByUserOperationSpec: coreClient.OperationSpec = {
  path: "/users/{userId}/devboxes",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBox,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListAllDevBoxesByUserExceptionHeaders,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [Parameters.endpoint, Parameters.userId],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAllDevBoxesNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBox,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListAllDevBoxesNextExceptionHeaders,
    },
  },
  urlParameters: [Parameters.endpoint, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCustomizationTaskDefinitionsByProjectNextOperationSpec: coreClient.OperationSpec =
  {
    path: "{nextLink}",
    httpMethod: "GET",
    responses: {
      200: {
        bodyMapper: Mappers.PagedCustomizationTaskDefinition,
      },
      default: {
        bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
        headersMapper:
          Mappers.DevBoxesListCustomizationTaskDefinitionsByProjectNextExceptionHeaders,
      },
    },
    urlParameters: [
      Parameters.endpoint,
      Parameters.projectName,
      Parameters.nextLink,
    ],
    headerParameters: [Parameters.accept],
    serializer,
  };
const listPoolsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedPool,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListPoolsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSchedulesByPoolNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedSchedule,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListSchedulesByPoolNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.poolName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSchedulesByProjectNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedSchedule,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListSchedulesByProjectNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listDevBoxesByUserNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBox,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListDevBoxesByUserNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listActionsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBoxAction,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListActionsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const delayActionsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBoxActionDelayResult,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesDelayActionsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listCustomizationGroupsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedCustomizationGroup,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper:
        Mappers.DevBoxesListCustomizationGroupsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listOperationsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBoxOperation,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListOperationsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listSnapshotsNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBoxSnapshot,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListSnapshotsNextExceptionHeaders,
    },
  },
  urlParameters: [
    Parameters.endpoint,
    Parameters.projectName,
    Parameters.userId,
    Parameters.devBoxName,
    Parameters.nextLink,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listAllDevBoxesByUserNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.PagedDevBox,
    },
    default: {
      bodyMapper: Mappers.AzureCoreFoundationsErrorResponse,
      headersMapper: Mappers.DevBoxesListAllDevBoxesByUserNextExceptionHeaders,
    },
  },
  urlParameters: [Parameters.endpoint, Parameters.userId, Parameters.nextLink],
  headerParameters: [Parameters.accept],
  serializer,
};
